// Component: IndicatorAboutSection.jsx
// Purpose: ONE shared "About <indicator>" disclosure (summary, how to read, output rows with the
//          chart-matched colour dot + documented sample, per-output expand) and ONE read-only
//          "Running configuration" list with the per-parameter "?" help. Both read the indicator
//          registry metadata served by /api/v1/indicators — the single docs authority — and are
//          rendered by the Strategy Builder properties panel AND the Live Analysis Indicators tab
//          (142-49 slice 6). State is local; parents pass `key={nodeId}` so a node switch remounts
//          both sections collapsed.
import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { defaultOutputStyles } from '../utils/chartDataDerivation';

//* 142-49 slice 5: a parameter shows a "?" ONLY when the registry carries real help text.
//*  A placeholder that merely repeats the name (most TA-Lib wrappers) hides the icon — the
//*  backend stays the one authority; the frontend decides from data quality.
export const hasParamHelp = (p) => {
    const d = (p?.description || '').trim().toLowerCase();
    if (!d) return false;
    const n = (p?.name || '').toLowerCase();
    return d !== n && d !== n.replace(/_/g, ' ') && d !== (p?.display_name || '').toLowerCase();
};
export const fmtParamValue = (v) => (v === null || v === undefined || v === '' ? null : String(v));

const findMeta = (allIndicatorMetadata, indicator) =>
    (allIndicatorMetadata || []).find(m => m.name === indicator) || {};

const toggleCls = 'w-full flex items-center gap-2 text-xs font-bold text-fg-2 uppercase tracking-wider text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] rounded';
const helpBtnCls = (open) => `w-4 h-4 rounded-full border text-[9px] font-bold leading-none flex items-center justify-center shrink-0 transition-colors ${open ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-[var(--divider)] text-fg-3 hover:text-fg-1'}`;

/*>>> ABOUT <<<*/
const IndicatorAboutSection = ({ nodeId, nodeData = {}, allIndicatorMetadata = [], displayName = '' }) => {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState({});
    const indMeta = findMeta(allIndicatorMetadata, nodeData.indicator);
    const outMeta = indMeta.visual_settings || {};
    const docs = indMeta.docs || null;
    if (!(outMeta.output_labels || docs)) return null;
    const outputList = nodeData.outputs || ['value'];
    const mergedStyles = { ...defaultOutputStyles(nodeData.indicator, allIndicatorMetadata, nodeData.visual_settings, nodeData.outputs), ...(nodeData.outputStyles || {}) };
    const bodyId = `indicator-about-body-${nodeId}`;
    const toggleId = `indicator-about-toggle-${nodeId}`;
    const Chevron = open ? FiChevronDown : FiChevronRight;
    return (
        <div data-testid="indicator-about" className="bg-bg-3 dark:bg-bg-2/50 p-4 rounded-xl border border-[var(--divider)]">
            <button type="button" id={toggleId} data-testid="indicator-about-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(o => !o)} className={toggleCls}>
                <Chevron aria-hidden="true" /> About {indMeta.display_name || displayName || nodeData.indicator}
            </button>
            <div id={bodyId} role="region" aria-labelledby={toggleId} hidden={!open} className="mt-2">
                {open && (
                    <>
                        {docs?.summary && <p className="text-xs text-fg-1 mb-1">{docs.summary}</p>}
                        {docs?.how_to_read && <p className="text-xs text-fg-3 mb-2">{docs.how_to_read}</p>}
                        <div className="text-xs font-bold text-fg-2 uppercase tracking-wider mb-1">Outputs</div>
                        {outputList.map(k => {
                            const od = docs?.outputs?.[k];
                            const rowOpen = !!rows[k];
                            const rowBodyId = `output-body-${nodeId}-${k}`;
                            const hasDetail = !!(od && (od.meaning || od.typical_range || od.how_to_read));
                            return (
                                <div key={k} data-testid={`output-row-${k}`} className="text-xs">
                                    <button type="button" data-testid={`output-toggle-${k}`} aria-expanded={rowOpen} aria-controls={rowBodyId} onClick={() => setRows(r => ({ ...r, [k]: !r[k] }))} className="w-full min-h-[32px] flex items-center gap-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] rounded">
                                        <span data-testid={`output-dot-${k}`} data-color={mergedStyles[k]?.color} className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: mergedStyles[k]?.color }} />
                                        <span className="font-medium truncate">{outMeta.output_labels?.[k] || k}</span>
                                        <span className="font-mono text-fg-3 truncate">{k}</span>
                                        {od?.sample && <span className="ml-auto font-mono text-fg-1 truncate">{od.sample}</span>}
                                        {rowOpen ? <FiChevronDown aria-hidden="true" className="shrink-0" /> : <FiChevronRight aria-hidden="true" className="shrink-0" />}
                                    </button>
                                    <div id={rowBodyId} hidden={!rowOpen} className="pl-5 text-[11px] text-fg-3 space-y-0.5 pb-1">
                                        {rowOpen && hasDetail && (
                                            <>
                                                {od.meaning && <div>Meaning: {od.meaning}</div>}
                                                {od.typical_range && <div>Range: {od.typical_range}</div>}
                                                {od.how_to_read && <div>Read: {od.how_to_read}</div>}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
};

/*>>> RUNNING CONFIGURATION (read-only) <<<*/
//* Monitoring surfaces only: plain definition list, no input chrome, the "?" is the only control;
//* the help shows the description alone (range/default matter for editing, not watching).
export const IndicatorConfigReadonly = ({ nodeId, nodeData = {}, allIndicatorMetadata = [] }) => {
    const [open, setOpen] = useState(false);
    const [helpKey, setHelpKey] = useState(null);
    const params = nodeData.params || {};
    const entries = Object.entries(params).filter(([k]) => k !== 'timeframe');
    if (entries.length === 0) return null;
    const paramMeta = findMeta(allIndicatorMetadata, nodeData.indicator).params || [];
    const bodyId = `indicator-config-body-${nodeId}`;
    const toggleId = `indicator-config-toggle-${nodeId}`;
    const Chevron = open ? FiChevronDown : FiChevronRight;
    return (
        <div data-testid="indicator-config" className="pt-3 border-t border-[var(--divider)]">
            <button type="button" id={toggleId} data-testid="indicator-config-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(o => !o)} className={toggleCls}>
                <Chevron aria-hidden="true" /> Running configuration
            </button>
            <div id={bodyId} role="region" aria-labelledby={toggleId} hidden={!open} className="mt-2">
                {open && (
                    <>
                        <p className="text-[10px] text-fg-3 mb-2">As deployed. Change in Strategy Builder.</p>
                        <dl className="space-y-1">
                            {entries.map(([key, value]) => {
                                const pMeta = paramMeta.find(pm => pm.name === key);
                                const help = hasParamHelp(pMeta);
                                const helpOpen = help && helpKey === key;
                                const helpId = `config-help-body-${nodeId}-${key}`;
                                return (
                                    <div key={key} data-testid={`config-row-${key}`} className="text-xs">
                                        <div className="flex items-center gap-2 min-h-[24px]">
                                            <dt className="text-[10px] font-bold text-fg-3 uppercase truncate">{key.replace(/_/g, ' ')}</dt>
                                            {help && (
                                                <button type="button" data-testid={`config-help-${key}`} aria-label={`About ${key.replace(/_/g, ' ')}`} aria-expanded={helpOpen} aria-controls={helpId} onClick={() => setHelpKey(helpOpen ? null : key)} className={helpBtnCls(helpOpen)}>?</button>
                                            )}
                                            <dd className="ml-auto font-mono text-fg-1 tabular-nums text-right truncate">{fmtParamValue(value) ?? '—'}</dd>
                                        </div>
                                        {helpOpen && (
                                            <div id={helpId} role="region" data-testid={`config-help-body-${key}`} className="mb-1 p-2 rounded-lg bg-bg-2 border border-[var(--divider)] text-[11px] text-fg-2">
                                                {pMeta.description}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </dl>
                    </>
                )}
            </div>
        </div>
    );
};

export default IndicatorAboutSection;
